import { action, computed } from "@ember/object"
import { inject as service } from "@ember/service"
import Component from "@glimmer/component"
import type { Course } from "@shared/models/course"
import type {
  Planbook,
  PlanbookDateAnnotationDateRange,
  PlanbookDateAnnotationSingle,
} from "@shared/models/planbook"
import type { RotationCalendar } from "@shared/models/rotation-calendar"
import { handleError } from "cc-frontend/error-handling"
import type { DateIs } from "cc-frontend/components/planner/modals/edit-annotation-body"
import { PLANBOOK_DATE_ANNOTATION_DELETE } from "cc-frontend/lib/actions/planbook/date/PLANBOOK_DATE_ANNOTATION_DELETE"
import type Dispatcher from "cc-frontend/services/dispatcher"
import type EmberStore from "cc-frontend/services/ember-store"
import type ModalManager from "cc-frontend/services/modal-manager"
import type PlanbookEditor from "cc-frontend/services/planbook-editor"
import { isNil, sortBy, toString } from "lodash-es"
import styles from "./list-modal.module.css"

export interface ListModalSignature {
  Args: {
    order: number
    closeModal: () => void
    model: {
      modalTitle: string
      planbook: Planbook
      rotationCalendar: RotationCalendar
    }
  }
}

export default class SpecialEventListModal extends Component<ListModalSignature> {
  readonly styles = styles

  @service("ember-store") store!: EmberStore

  @service dispatcher!: Dispatcher
  @service modalManager!: ModalManager
  @service planbookEditor!: PlanbookEditor

  @computed("args.model.planbook.attributes.calendar.dateAnnotations.[]")
  get planbookDateAnnotations() {
    if (isNil(this.args.model?.planbook)) return []
    let allDateAnnotations = this.args.model.planbook.attributes.calendar.dateAnnotations
    return sortBy(allDateAnnotations, (annotation) => {
      if (annotation.type === "date-annotation-planbook-custom-single") {
        return annotation.attributes.dateString
      } else if (annotation.type === "date-annotation-planbook-custom-range") {
        return annotation.attributes.startDate
      }
      // To return a default sort value -- we shouldn't hit this
      console.error("Handled a date annotation we don't have a sort key for.")
      return annotation.id
    })
  }

  @action
  async addAnEvent() {
    let schoolYear = await this.store.find(
      "school-year",
      this.args.model.planbook.attributes.schoolYearId
    )
    // We allow special events to be made as early as Jan 1 of the first school year in case they want an event before their planbook starts
    let firstDay = toString(schoolYear.attributes.years[0]).concat("-01-01")

    const courses = await Promise.all(
      this.args.model.planbook.relationships.courses.data
        .filter((relation) => !!relation.id)
        .map((relation) => this.store.find("course", relation.id) as Promise<Course>)
    )

    this.modalManager.openModal("planner/modals/edit-annotation", {
      allowRangeToggle: false,
      allowNoClassToggle: true,
      datesAreEditable: true,
      description: null,
      title: null,
      startDate: null,
      endDate: null,
      minimumStartDate: firstDay,
      modalTitle: "Add a Special Event or Day Off",
      submitButtonText: "Create",
      dateIs: "FLEXIBLE_RANGE",
      submitAction: ({ title, description, startDate, endDate, isOff }) => {
        this.planbookEditor.addAnnotation({
          title: title,
          description: description,
          startDate: startDate,
          endDate: endDate,
          isOff: Boolean(isOff),
          models: { ...this.args.model, courses },
          // Always bump if there would be dangling lessons, to prevent surprises (erasing / hiding).
          bumpDeleteOrNothing: isOff ? "BUMP" : "NOTHING",
        })
      },
    })
  }

  @action
  async openEditAnnotationModal(
    oldAnnotation: PlanbookDateAnnotationSingle | PlanbookDateAnnotationDateRange
  ) {
    // isRange, startDate, endDate, dateIs, schoolYear, and firstDay supply info to the modal
    // to make the calendar in the modal function properly
    let isRange = oldAnnotation.type === "date-annotation-planbook-custom-range"
    let startDate =
      oldAnnotation.type === "date-annotation-planbook-custom-range"
        ? oldAnnotation.attributes.startDate
        : oldAnnotation.attributes.dateString
    let endDate =
      oldAnnotation.type === "date-annotation-planbook-custom-single"
        ? null
        : oldAnnotation.attributes.endDate
    let dateIs: DateIs = isRange ? "FLEXIBLE_RANGE" : "SINGLE_DATE"
    let schoolYear = await this.store.find(
      "school-year",
      this.args.model.planbook.attributes.schoolYearId
    )
    // We allow special events to be made as early as Jan 1 of the first school year in case they want an event before their planbook starts
    let firstDay = toString(schoolYear.attributes.years[0]).concat("-01-01")

    const courses = await Promise.all(
      this.args.model.planbook.relationships.courses.data
        .filter((relation) => !!relation.id)
        .map((relation) => this.store.find("course", relation.id) as Promise<Course>)
    )

    this.modalManager.openModal("planner/modals/edit-annotation", {
      allowRangeToggle: false,
      allowNoClassToggle: true,
      dateIs: dateIs,
      datesAreEditable: true,
      description: oldAnnotation.attributes.description,
      endDate: endDate,
      classIsOff: oldAnnotation.attributes.isOff,
      minimumStartDate: firstDay,
      modalTitle: "Edit Special Event",
      startDate: startDate,
      title: oldAnnotation.attributes.title,
      submitButtonText: "Update",
      submitAction: ({ title, description, endDate, startDate, isOff }) => {
        this.planbookEditor.updateAnnotation({
          oldAnnotation: oldAnnotation,
          title: title,
          description: description,
          startDate: startDate,
          endDate: endDate,
          isOff: Boolean(isOff),
          models: { ...this.args.model, courses },
          // Always bump if there would be dangling lessons, to prevent surprises (erasing / hiding).
          bumpDeleteOrNothing: isOff ? "BUMP" : "NOTHING",
        })
      },
    })
  }

  @action
  deleteEvent(annotation) {
    swal({
      title: "Remove Special Event?",
      text: "This will also remove the yellow note.",
      type: "warning",
      showCancelButton: true,
    })
      .then(() => {
        this.dispatcher.dispatchAction(PLANBOOK_DATE_ANNOTATION_DELETE, {
          annotation: annotation,
          planbook: this.args.model.planbook,
          calledFrom: "planner/planbook/special-events/list-modal",
        })
      })
      .catch((error) => {
        if (error instanceof Error) handleError(error)
      })
  }
}
